/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import jp from 'jsonpath';
import i18next from 'i18next';
import _ from 'lodash';
import mime from 'mime-types';
import { shortFormatDate } from './dateUtils';
import { getEnumName } from './validationSchemaUtils';
import { capitalizeWords, twoDecimalsFormatter } from './stringUtils';

export const isEmpty = (obj) => {
  return obj == null || Object.keys(obj).length === 0;
};

export const isEmptyArray = (array) => {
  return array == null || !array || (Array.isArray(array) && !array.length);
};

export const isDate = (key, value) => {
  return key.indexOf('Date') >= 0 && new Date(value) !== 'Invalid Date' && !Number.isNaN(new Date(value));
};

export const flattenObject = (obj, path = []) =>
  !_.isObject(obj)
    ? { [path.join('.')]: obj }
    : _.reduce(obj, (cum, next, key) => _.merge(cum, flattenObject(next, [...path, key])), {});

export const nestedObjectFromFlattenObject = (mapping, value, obj) => {
  const indexOfFirstDot = mapping.indexOf('.');

  if (indexOfFirstDot > -1) {
    const firstMapping = mapping.substring(0, indexOfFirstDot);
    const restOfMapping = mapping.substring(indexOfFirstDot + 1);

    if (!obj[firstMapping]) {
      obj[firstMapping] = {};
    }

    obj[firstMapping] = nestedObjectFromFlattenObject(restOfMapping, value, obj[firstMapping]);
  } else {
    obj[mapping] = value;
  }

  return obj;
};

/**
 * Clones the source object by skipping empty arrays, objects, null and empty string properties.
 * @param {*} obj source object
 * @returns sanitized clone
 */
export const sanitize = (obj = {}) => {
  let newObj = {};

  function sanitizeArray(key) {
    if (key !== 'undefined' && key !== null) {
      newObj[key] = obj[key].map((k) => {
        return sanitize(k);
      });
    }
  }

  function sanitizeObject(key) {
    newObj[key] = sanitize(obj[key]);
    if (isEmpty(newObj[key])) {
      delete newObj[key];
    }
  }

  function sanitizeValue(key) {
    newObj[key] = obj[key];
    if (newObj[key] == null || newObj[key] === '') {
      delete newObj[key];
    }
  }
  // check if the type of object is a string otherwise
  // the Object.keys will transform the value 'string' into an array: {'s', 't', 'r', 'i', 'n', 'g'}
  if (typeof obj !== 'string') {
    Object.keys(obj).forEach((key) => {
      if (Array.isArray(obj[key])) {
        sanitizeArray(key);
      } else if (obj[key] && typeof obj[key] === 'object' && obj[key].constructor === Object) {
        sanitizeObject(key);
      } else if (obj[key] != null) {
        sanitizeValue(key);
      }
    });
  } else {
    // set the string value to the newObj or it will become undefined.
    newObj = obj;
  }
  return newObj;
};

export const confirmRowsIfNotDirty = (draft) => {
  const returnDraft = { ...draft };
  if (returnDraft.dirtyCheckRow) {
    let removeInventor = false;
    for (let counter = returnDraft.parties.length - 1; counter >= 0; counter -= 1) {
      const party = returnDraft.parties[counter];
      if (!party.state) {
        const checkParty = {
          ...party,
          id: returnDraft.dirtyCheckRow.id,
          state: returnDraft.dirtyCheckRow.state,
        };
        if (
          JSON.stringify(checkParty, Object.keys(checkParty).sort()) ===
          JSON.stringify(returnDraft.dirtyCheckRow, Object.keys(returnDraft.dirtyCheckRow).sort())
        ) {
          party.id = returnDraft.dirtyCheckRow.id;
          party.state = returnDraft.dirtyCheckRow.state;
        } else if (party.isInventor) {
          removeInventor = true;
        }
      }
    }
    if (removeInventor) {
      for (let counter = returnDraft.parties.length - 1; counter >= 0; counter -= 1) {
        if (returnDraft.parties[counter].id === returnDraft.dirtyCheckRow.id) {
          returnDraft.parties.splice(counter, 1);
        }
      }
    }
  }
  return returnDraft;
};

export const overlaps = (ranges) => {
  let overlap = false;
  for (let i = 0; i < ranges.length - 1; i += 1) {
    const selected = ranges[i];
    for (let j = i + 1; j < ranges.length; j += 1) {
      const range = ranges[j];
      overlap =
        range.start === selected.start || range.end === selected.end || range.start > selected.start
          ? range.start <= selected.end
          : range.end >= selected.start;
      if (overlap) {
        break;
      }
    }
    if (overlap) {
      break;
    }
  }
  return overlap;
};

const hasGap = (range, previousValue) => {
  return previousValue && range.start - previousValue.end > 1;
};

export const isLockError = error => error?.data?.error?.errorCode === 'lock' || false;

export const getSearchPageByProcedure = (history) => {
  const pathName = history?.location?.pathname;
  return pathName?.split('/')?.length > 1 ? `/${pathName.split('/')[1]}/search` : '/';
};

export const hasGaps = (ranges) => {
  if (ranges.length > 0 && ranges[0].start > 1) return true;
  return ranges.some((range) => {
    const prevIndex = ranges.indexOf(range) - 1;
    let previousValue;
    if (prevIndex > -1) {
      previousValue = ranges[prevIndex];
    }
    return hasGap(range, previousValue);
  });
};

export const propertiesToArray = (object) => {
  if (object == null) return [];

  const isObject = val => typeof val === 'object' && !Array.isArray(val);

  const addDelimiter = (a, b) => (a ? `${a}.${b}` : b);

  const paths = (obj = {}, head = '') => {
    if (obj == null) return head;
    return Object.entries(obj).reduce((product, [key, value]) => {
      const fullPath = addDelimiter(head, key);
      return isObject(value) ? product.concat(paths(value, fullPath)) : product.concat(fullPath);
    }, []);
  };

  return paths(object);
};

function transformJson(json, pattern, transformFn) {
  // we need to escape backslashes and tabulation because they are an allowed input character but break the json.
  // we cannot use JSON.stringify() here because some content (e.g. countries) is already stringified and
  // would lead to double double quotes in the json (e.g. ""hello"").
  function escapeBackslashes(content) {
    let newContent = content;
    newContent = content?.replace?.(/\\/g, '\\\\') ?? newContent;
    newContent = newContent?.replace?.(/\t/g, '\\t"') ?? newContent;
    newContent = newContent?.replace?.(/"/g, '\\"') ?? newContent;
    return newContent?.replace?.(/\n/g, '\\n') ?? newContent;
  }

  function replacePlaceholderWithActualValue(argsString) {
    const args = argsString.split(',').map(arg => arg.trim());
    return escapeBackslashes(transformFn(...args));
  }

  function getPlaceholder(placeholderKey) {
    return placeholderKey.substring(placeholderKey.indexOf('(') + 1, placeholderKey.indexOf(')'));
  }

  let string = JSON.stringify(json);

  function transform() {
    let match;
    const transformed = {};
    do {
      match = pattern.exec(string);
      if (match) {
        const placeholderKey = match[0];
        const placeholder = getPlaceholder(placeholderKey);
        if (placeholder) {
          transformed[placeholderKey] = replacePlaceholderWithActualValue(placeholder);
        }
      }
    } while (match);
    return transformed;
  }

  try {
    const transformed = transform();
    Object.entries(transformed).forEach(([key, val]) => {
      string = string.split(key).join(val);
    });
    return JSON.parse(string);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Error occurred while translating schema for input ${string}`, err);
    return null;
  }
}

export function getPathsToPropertyAsArray(object = {}, property, withoutLeaf = false) {
  return jp.paths(object, `$..['${property}']`).map((match) => {
    if (match.length > 1) match.shift();
    if (withoutLeaf) {
      match.pop();
    }
    return match;
  });
}

export function getPathsToProperty(object, property, separator = '.') {
  return jp.paths(object, `$..['${property}']`).map((match) => {
    if (match.length > 1) match.shift();
    return match.join(separator);
  });
}

export function getPathsToPropertyWithValue(object, property, value, separator = '.') {
  return jp.paths(object, `$..[?(@.${property}=='${value}')]`).map((match) => {
    if (match.length > 1) match.shift();
    return match.join(separator);
  });
}

/**
 * Translates json using convention
 * @param json json object to transform
 * @returns {null|any} transformed json
 */
export function lookupI18n(json = {}) {
  return transformJson(json, /i18n\(.+?\)/g, x => i18next.t(x));
}

export function lookupCapitalizeWords(json = {}) {
  return transformJson(json, /capitalizeWords\(.+?\)/g, x => capitalizeWords(x));
}

/**
 * Replaces itemShortDate(...) ocurrences on the json by short formatted dates
 * @param json json object to transform
 * @param item object contained the properties which values will be replaced on the json
 * @param defaultValue string hosting the default value if the field is empty
 * @returns {null|any} transformed json
 */
export function lookupItemShortDateProperties(json, item, defaultValue = '') {
  const transformFn = (string, format) => {
    const value = string.split('.').reduce((acum, n) => acum[n] ?? '', item);
    const formattedValue = value && shortFormatDate(value, format);
    return formattedValue || defaultValue;
  };

  return transformJson(json, /itemShortDate\(.+?\)/g, transformFn);
}

/**
 * Aggregates together entries which have the type 'miscellaneous' into
 * a single entry without title and with the aggregated titles of the
 * miscellaneous entries as a single value
 * @param {*} entries json object containing an array of json objects
 * @returns {null|any} transformed entries json array
 */
export function lookupMiscellaneousItemsForAggregation(entries) {
  const miscEntries = entries.filter(arrEl => arrEl.type === 'miscellaneous');

  if (miscEntries.length > 0) {
    miscEntries.forEach(miscEntry => entries.splice(entries.indexOf(miscEntry), 1));
    const miscAggregationValue = miscEntries.map(miscEntry => miscEntry.title).join('\n');
    entries.forEach((arrEl) => {
      if (arrEl.type === 'miscellaneousAggregation') {
        arrEl.value = miscAggregationValue;
        arrEl.type = '';
      }
    });
  }

  return entries;
}

/**
 * Replaces item(...) occurrences on the json by properties of item.
 * It can traverse multiple levels.
 * @param json json object to transform
 * @param item object contained the properties which values will be replaced on the json
 * @param defaultValue string hosting the default value if the field is empty
 * @returns {null|any} transformed json
 */
export function lookupItemProperties(json, item, defaultValue = '') {
  const transformFn = string => string.split('.').reduce((acum, n) => acum[n] ?? '', item) || defaultValue;

  return transformJson(json, /item\(.+?\)/g, transformFn);
}

/**
 * Replaces context(...) occurrences on the json by properties of context.
 * It can traverse multiple levels.
 * @param json json object to transform
 * @param context external context data
 * @param defaultValue string hosting the default value if the field is empty
 * @returns {null|any} transformed json
 */
export function lookupContextProperties(json, context, defaultValue = '') {
  if (!context) return json;
  const transformFn = string => _.get(context, string, defaultValue);

  return transformJson(json, /context\(.+?\)/g, transformFn);
}

/**
 * Replaces itemFloat(...) ocurrences on the json by decimal point if found, otherwise returns the default value
 * It can traverse multiple levels.
 * @param json json object to transform
 * @param item object contained the properties which values will be replaced on the json
 * @param defaultValue string hosting the default value if the field is empty
 * @returns {null|any} transformed json
 */
export function lookupItemFloatProperties(json, item, defaultValue = '') {
  const amountFormatter = twoDecimalsFormatter(i18next.language);

  const transformFn = (string) => {
    const value = string.split('.').reduce((acum, n) => acum[n] ?? '', item);
    if (value && !Number.isNaN(Number.parseFloat(value)) && /^(\d(?:\.?\d)*)?$/.test(value)) {
      return amountFormatter.format(value);
    }
    return defaultValue;
  };

  return transformJson(json, /itemFloat\(.+?\)/g, transformFn);
}

/**
 * Replaces itemI18n(...) occurrences on the json by translated properties of item.
 * It can traverse multiple levels.
 * @param json json object to transform
 * @param item object contained the properties which values will be replaced on the json
 * @param defaultValue string hosting the default value if the field is empty
 * @returns {null|any} transformed json
 */
export function lookupItemI18nProperties(json, item, defaultValue = '') {
  const transformFn = string => i18next.t(string.split('.').reduce((acum, n) => acum[n] ?? '', item)) || defaultValue;

  return transformJson(json, /itemI18n\(.+?\)/g, transformFn);
}

/**
 * Replaces itemBoolean(...) ocurrences on the json by yes/- if found
 * It can traverse multiple levels.
 * @param json json object to transform
 * @param item object contained the properties which values will be replaced on the json
 * @returns {null|any} transformed json
 */
export function lookupItemBooleanProperties(json, item) {
  const transformFn = (string) => {
    const value = string.split('.').reduce((acum, n) => acum[n] ?? '', item);
    if (value === true) {
      return i18next.t('yes');
    }
    return '-';
  };
  return transformJson(json, /itemBoolean\(.+?\)/g, transformFn);
}

/**
 * Replaces itemNameI18n(propertyPath, enumDefinitionName) occurrences on the input json with the
 * translated properties of the enum name from the definition in schema.jsonschema.
 * It can traverse multiple levels.
 *    Note: Make sure the enumeration is declared as a definition of the schema.jsonschema!
 *
 *    uiSchema example: itemNameI18n(personalDetails.nationality, country)
 *
 * @param json source json object that contains one or more itemNameI18n() tokens
 * @param entity object containing the enum properties which should be translated
 * @param schema full jsonschema (containing definitions)
 * @param defaultValue string hosting the default value if the field is empty
 * @returns {null|any} transformed json
 */
export function lookupItemNameI18nProperties(json, entity, schema, defaultValue = '') {
  const itemNameI18nObj = {};
  const pattern = /itemNameI18n\(.+?\)/g;
  const getPathValue = (_entity, _path) => _path.split('.').reduce((acum, n) => acum[n] ?? '', _entity);
  let str = JSON.stringify(json);
  let match = pattern.exec(str);

  while (match !== null) {
    const itemNameI18n = match[0];

    const enumPathParam = itemNameI18n?.substring(itemNameI18n?.indexOf('(') + 1, itemNameI18n?.indexOf(',')).trim();
    const enumDefinitionParam = itemNameI18n
      ?.substring(itemNameI18n?.indexOf(',') + 1, itemNameI18n?.indexOf(')'))
      .trim();
    const enumValue = getPathValue(entity, enumPathParam);
    const enumNameValue = getEnumName(enumValue, enumDefinitionParam, schema);

    const defaultEnumValue = defaultValue || '-'; // Keep backwards compatibility
    itemNameI18nObj[itemNameI18n] = enumValue === '' ? defaultEnumValue : lookupI18n(enumNameValue);
    match = pattern.exec(str);
  }

  try {
    Object.entries(itemNameI18nObj).forEach(([key, val]) => {
      str = str.split(key).join(val);
    });
    return JSON.parse(str);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Error occurred while translating schema for input ${str}`, err);
    return null;
  }
}

/**
 * Replaces itemExtraLanguages(...) occurrences on the json by occurrences in main object concatenate with occurrences in the extraLanguages array.
 * It can traverse multiple levels.
 * @param json json object to transform
 * @param item object contained the properties which values will be replaced on the json
 * @param defaultValue string hosting the default value if the field is empty
 * @returns {null|any} transformed json
 */
export function lookupitemExtraLanguagesProperties(json, item, defaultValue = '') {
  const transformFn = (...keys) => {
    const objectArray = item.extraLanguages;
    let mainLanguageValue;
    const itemValues = [];
    keys.forEach((key) => {
      const itemKeyValue = key.split('.').reduce((acum, n) => acum[n] ?? '', item) || defaultValue;
      mainLanguageValue = mainLanguageValue ? mainLanguageValue.concat(' ').concat(itemKeyValue) : itemKeyValue;
      if (objectArray) {
        objectArray.forEach((secondaryLanguage, index) => {
          const keyValue = key.split('.').reduce((acum, n) => acum[n] ?? '', secondaryLanguage);
          if (keyValue) {
            itemValues[index] = itemValues[index] ? itemValues[index].concat(' ').concat(keyValue) : keyValue;
          }
        });
      }
    });

    itemValues.unshift(mainLanguageValue);

    return itemValues.join('\\');
  };
  return transformJson(json, /itemExtraLanguages\(.+?\)/g, transformFn);
}

/**
 * Replaces contextAttachment(...) occurrences on the json by properties of item.
 * It can traverse multiple levels.
 * @param json json object to transform
 * @param item object contained the properties which values will be replaced on the json
 * @param context external context data
 * @returns {null|any} transformed json
 */
export function lookupContextAttachmentProperties(json, item, context) {
  const transformFn = (_attachmentType, _uploaderIdKey) => {
    const _uploaderId = _.get(item, _uploaderIdKey);
    const attachment = _.get(context, 'submission.attachments', []).find(
      ({ attachmentType, uploaderId }) => attachmentType === _attachmentType && uploaderId === _uploaderId,
    );
    if (!attachment) return '';

    // we must return a stringified json because transformJson works on string, not object
    // also add the submissionId needed for previewing attachments
    const submissionId = _.get(context, 'submission.id');
    return JSON.stringify({ ...attachment, submissionId });
  };

  return transformJson(json, /contextAttachment\(.+?\)/g, transformFn);
}

/**
 * Replaces contextFacsimileSignatureAttachment(...) occurrences on the json by properties of item.
 * It can traverse multiple levels.
 * @param json json object to transform
 * @param item object contained the properties which values will be replaced on the json
 * @param context external context data
 * @returns {null|any} transformed json
 */
export function lookupContextFacsimileSignatureAttachmentProperties(json, item, context) {
  const transformFn = (signatureFileName, signatureFileId) => {
    const submissionId = _.get(context, 'submission.id');
    const id = _.get(item, signatureFileId);
    const originalFileName = _.get(item, signatureFileName);
    // we must return a stringified json because transformJson works on string, not object
    // also add the submissionId needed for previewing attachments
    return JSON.stringify({
      submissionId,
      id,
      originalFileName,
      mimeType: mime.lookup(originalFileName),
    });
  };

  return transformJson(json, /contextFacsimileSignatureAttachment\(.+?\)/g, transformFn);
}

/**
 * Replaces contextFacsimileSignatureAttachment(...) occurrences on the json by properties of item.
 * It can traverse multiple levels.
 * @param json json object to transform
 * @param item object contained the properties which values will be replaced on the json
 * @param context external context data
 * @returns {null|any} transformed json
 */
export function lookupContextLegacyPartyProperties(json, item, context) {
  const transformFn = (signerPartyIdPath, partiesPath, partyNamePath) => {
    const signerPartyId = _.get(item, signerPartyIdPath, '');
    const parties = _.get(context, `submission.${partiesPath}`);
    const party =
      Object.values(parties)
        ?.flat()
        .find(_party => _party?.id === signerPartyId) ?? {};
    return _.get(party, partyNamePath, '');
  };

  return transformJson(json, /contextLegacyParty\(.+?\)/g, transformFn);
}

/**
 * Sorts a collection by a given field and in a specific order
 * @param {Array} collection the collection to sort
 * @param {Object} config object containing the sort configuration
 * @param {String} language the locale to be used to compare two items
 * @returns the collection sorted by the given field and in the specific order provided
 */
export const sortCollection = (collection, config, language) => {
  // Returns the original collection if there is nothing to sort, or if there is no field provided
  if (!collection?.length || !config?.field) return collection;

  const { field, reverse } = config;
  return collection.slice().sort((a, b) => {
    const comparison = language
      ? `${a[field]}`.localeCompare(b[field], language)
      : `${a[field]}`.localeCompare(b[field]);
    return reverse ? -1 * comparison : comparison; // Change order if stated
  });
};

/**
 * find field in formData bu Id
 * @param {Object} formData the formData object
 * @param {String} keys the String object that includes keys
 * @returns field object
 */
export const searchFieldById = (formData, keys) => {
  if (!formData) {
    return null;
  }
  let _result = formData;
  for (const _id of keys?.split('_')) {
    if (_result != null) {
      _result = _result[_id];
    }
  }
  return _result;
};
